import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import { fetchStockTransferDetailsData } from "../../../services/stock-service";
import Aggrid from "../../../framework/AgGrid";
import classes from "./index.module.css";
import moment from "moment";
import { displayToast } from "../../../framework/toast/toastUtils";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import { getStringValue } from "../../../utils/utils";
import GlobalContext from "../../../framework/GlobalContext";
import { selectRow, rowDown, rowUp } from "../../../utils/gridUtils";

const StockTransferDetails = (props) => {
  const { stockTransferId = null, onEscape = () => {} } = props;
  const [transferData, setTransferData] = useState(null);
  const [rowData, setRowData] = useState(null);
  const[api,setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Stock Transfer Item Id",
        field: "stockTransferItemId",
        width: 175,
      },
      {
        headerName: "Batch",
        field: "batch.batchNumber", 
        width: 150,
      },
      {
        headerName: "Product ID",
        field: "medicineId",
        width: 150,
      },
      {
        headerName: "Product",
        field: "medicine.medicineNameDetailed",
        width: 450,
      },
      {
        headerName: "Transfered Quantity",
        field: "transferedQuantity",
        width: 200,
        showTotal: true,
      },
      {
        headerName: "Accepted Quantity",
        field: "expectedQuantity",
        width: 150,
        editable: false,
        showTotal: true,
        cellEditor: "cellNumberEditor",
        cellRendererFramework: (params) => {
          return params.colDef.editable ? (
            <div className="ag-editable-cell">
              {getStringValue(params.data.expectedQuantity)}
            </div>
          ) : (
            params.data.expectedQuantity
          );
        },
        cellEditorParams: (params) => {
          return {
            decimalScale: 0,
            value: params.data.expectedQuantity,
            onChange: (values, e) => {
              if (values.value > params.data.transferedQuantity) {
                displayToast({
                  type: "error",
                  title: `Quantity cannot be greater than ${params.data.transferedQuantity}`,
                });
                params.data.transferedQuantity = params.data?.quantity;
                api.redrawRows()
                return;
              }
              params.data.expectedQuantity = values.value;
            },
          };
        },
      },
      {
        headerName: "Expiry Date",
        field: "batch.batchExpiry",
        width: 150,
        valueFormatter: (params) => {
          return moment(params.value).format("DD/MM/YYYY");
        },
      },
      {
        headerName: "Remarks",
        field: "remarks",
        width: 150,
      },
    ];
  }, [api]);

  const getStockTransferDetails = useCallback((stockTransferId) => {
    fetchStockTransferDetailsData(`_id=${stockTransferId}`).then((resp) => {
      const data = resp?.result?.rows;
      setTransferData(data);
      const updatedRowData = data?.stockTransferItems?.map((item) => ({
        ...item,
        expectedQuantity: item?.transferedQuantity || 0,
      }));

      setRowData(updatedRowData);
    });
  }, []);

  const onGridReadyHandler = useCallback((params)=>{
    setApi(params.api)
    params.api.sizeColumnsToFit();
    selectRow(params.api, selectedRowIndex)
  }, [selectedRowIndex]);

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api)
        return;
      }
      if (key === "arrowup") {
        rowUp(api)
        return;
      }
    },
    [api, onEscape]
  );

  useEffect(() => {
    if (stockTransferId) {
      getStockTransferDetails(stockTransferId);
    }
  }, [getStockTransferDetails, stockTransferId]);

  useEffect(() => {
    setCurrentComponent("grid_stock_transfer_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_stock_transfer_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["stock-transfer-details"]}>
          <div className={classes["stock-transfer-details-header"]}>
            <div className={classes["stock-transfer-details-header-left"]}>
              <span>
                Stock Transfer Details - {transferData?.stockTransferId}
              </span>

              <span>From Branch - {transferData?.fromBranchId}</span>
              <span>To Branch - {transferData?.branchId}</span>
            </div>
            <span className={classes["stock-transfer-date"]}>
              Transfered Date:{" "}
              {moment(`${transferData?.businessDate}`).format(
                "DD/MM/YYYY hh:mm A"
              )}
            </span>
          </div>

      <div className={classes["stock-transfer-details-grid"]}>
        <Aggrid
          id="grid_stock_transfer_details"
          className="ag-theme-balham"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={
            (params) => {
              setTimeout(() => {
                onGridReadyHandler(params);
              }, 100);
            }
          }
          onCellClicked={(params) => {
              setSelectedRowIndex(params.rowIndex);
            }
          }
        />
      </div>
    </div>
  );
};

export default StockTransferDetails;
